/*
 * Validate and import DAO functions for Spreadsheets. Each is called with spreadsheet params and
 * any flow-specific params: e.g. validateSpreadsheetForX(spreadsheetParams, flowParam1, flowParam2, ...)
 *
 * Spreadsheet params consist of:
 * @param  {String}   filename               S3 location of the spreadsheet (URL)
 * @param  {String}   location               filename of the spreadsheet, for CSV vs. XSL detection
 * @param  {String[]} columnTypes            column types for file
 *                                           (SpreadsheetBulkImportConstants.COLUMN_TYPE)
 * @param  {Number}   _timeLimit             Optional time limit for Celery task
 */
import {DataModificationsHeader} from 'audit/DataModifications';
import {SpreadsheetColumnsMapping} from 'bulk-import/panes/SaveSpreadsheetColumnsMappingForm';
import {
  COLUMN_TYPE,
  ColumnTransform,
  DerivedColumnExampleValue,
  ValidateSpreadsheetForBulkImportResponse,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import {RegistryImportSettings} from 'bulk-import/SpreadsheetBulkImportStore';
import {FolderSummary} from 'constants/FolderConstants';
import {
  BioentityAliasInfo,
  Registry,
  SchemalessBulkImportSupportedOligoAwareTypes,
} from 'constants/RegistryConstants';
import {GraphQLTypes} from 'graphql-client/declarative-translations/external/GraphQLTypes';
import {
  DataTableConfig,
  ImportedRow as ImportedDataTableRow,
  ImportedRowUpdate,
} from 'notebook/data-tables/DataTableConstants';
import {ImportedStructuredTableRow} from 'notebook/structured-tables/spreadsheet-import/prepareImportedRows';
import {ImportedTransferTableRow} from 'notebook/transfer-tables/TransferTableUtil';
import APIUtil from 'util/APIUtil';
import * as ImportUtil from 'util/ImportUtil';
import * as net from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';
import {Optional} from 'util/Types';
import {ImportedRow} from 'workflows/samples/makeSampleGroup';

import {MonomerMapping} from './panes/MonomerMappingForm';

const MS_PER_MINUTE = 1000 * 60;

/**
 * Max time for trial sample ingestion calculated as:
 * - Maximum 6000 rows
 * - ~12-14 shards @ ~500 rows per shard (REGISTRY_TRIAL_SAMPLE_INGESTION_SHARD_SOFT_MAX_ROWS_PER_SHARD)
 * - Each shard has max_wait_time=Medium (2 minutes)
 * - Each shard has max_processing_time=Medium (3 minutes)
 * - 1 gather task
 * - Gather task has max_wait_time=Low (20s - 1/3 minute)
 * - Gather task has max_processing_time=Small (1 minute)
 * = 14 * (2+3) + 1 * (1 + 20s)
 * = 71:20 in a worst-case scenario
 *
 * In practice we expect to not approach this limits and to gain benefits from parallel shard execution.
 * 20 minutes is likely sufficient for P95 of cases
 */
export const CREATE_TRIAL_SAMPLE_MAX_TOTAL_WAIT_TIME_MS = 20 * MS_PER_MINUTE;

/*
 * Uploads a file to S3 and asynchronously returns the S3 URL.
 * @param  {File} file  file to upload
 * @return {Promise}    promise that resolves with the URL of the uploaded file
 */
export function importFile(file: File): JQueryPromise<string> {
  return ImportUtil.upload(file);
}

/**
 * Returns the column names found in a spreadsheet
 * @param  {String} filename  filename of the spreadsheet, for CSV vs. XSL detection
 * @param  {String} location  S3 location of the spreadsheet (URL)
 * @param  {Boolean} inferDataTypes  whether to infer column data types (optional)
 */
export function parseSpreadsheet(
  filename: string,
  location: string,
  inferDataTypes?: boolean
): JQueryPromise<{columns: Array<string>; row_count: number; data_types?: Array<string>}> {
  return net.postJSON({
    url: '/1/api/files/get-spreadsheet-columns',
    data: {filename, location, inferDataTypes},
  });
}

export interface ParseSDFResponse {
  columnNames: Array<string>;
  updatedUrl: string;
}

/**
 * Returns the updated S3 url with the converted SD file and the column names found in the SD file.
 * @param  {String} filename  filename of the SD file, used for CSV upload to S3.
 * @param  {String} location  S3 location of the SD file (URL)
 */
export function parseSDF(filename: string, location: string): JQueryPromise<ParseSDFResponse> {
  return net.postJSON({url: '/1/api/molecules/parse-sdf', data: {filename, s3_location: location}});
}

// This interface represents an additional column that was derived from the current spreadsheet.
interface DerivedColumnResponse {
  exampleValues: Array<DerivedColumnExampleValue>;
  // The allowed types for derived column, see
  // benchling.bulk_adapters.derived_columns.derived_column_transform
  columnTypes: Array<COLUMN_TYPE>;
  // The allowed schema field IDs for derived column, if one of the `columnTypes` is COLUMN_TYPE.SCHEMA_FIELD;
  // computed by looking at the values in the derived column and checking which schema fields would accept the
  // values; used to ensure that the user can only choose schema fields that will not cause validation errors
  // when mapping derived columns.
  compatibleSchemaFieldIds?: Array<string>;
  // Number of rows that contain a non-null derived value.
  numRowsWithDerivedValues: number;
}

export type FindDerivedColumnsResponse = {
  [key in ColumnTransform]: DerivedColumnResponse;
};

interface SpreadsheetParams {}

export function findDerivedColumns(
  spreadsheetParams: SpreadsheetParams,
  folderId: string | null,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: Optional<string>,
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes | null,
  areSequencesCircular: boolean,
  schemaFieldIdsByColumnName: {[key: string]: number},
  customSyntaxName?: string
): JQueryPromise<FindDerivedColumnsResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/inventory-files:find-derived-columns-for-bulk-create',
    data: {
      ...spreadsheetParams,
      folderId,
      linkItemsFolderIds,
      tagSchemaId,
      schemalessCreationEntityType,
      areSequencesCircular,
      schemaFieldIdsByColumnName,
      customSyntaxName,
    },
  });
}

/**
 * Performs dry-run: creating inventory files from spreadsheet into folder, returning any errors that would
 * result.
 * @param  {String}   folderId            API ID of destination folder
 * @param  {String[]} linkItemsFolderIds  api identifiers for folders whose files may be linked
 * @param  {String}   tagSchemaId         api identifier of the tag schema that the imported entites will
 *                                        be given
 * @param  {Boolean}  areSequencesCircular     create sequences with circular topology
 * @param  {String}   schemalessCreationEntityType     entity type to validate against, alternative to tagSchemaId
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param {Object}    monomerMapping      mapping of natural base to monomer ID for adding mods to sequences
 */
export function validateSpreadsheetForCreateInventoryFiles(
  spreadsheetParams: SpreadsheetParams,
  folderId: string,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: Optional<string>,
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes | null,
  areSequencesCircular: boolean,
  containerSchemaId: Optional<string>,
  schemaFieldIdsByColumnName: {[key: string]: number},
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE},
  monomerMapping: MonomerMapping | null,
  complexPolymerComponentColumnMapping: {[key: string]: string},
  customSyntaxName?: string
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/inventory-files:validate-for-bulk-create',
    data: {
      ...spreadsheetParams,
      folderId,
      linkItemsFolderIds,
      tagSchemaId,
      schemalessCreationEntityType,
      areSequencesCircular,
      containerSchemaId,
      schemaFieldIdsByColumnName,
      derivedColumnsMapping,
      monomerMapping,
      customSyntaxName,
      complexPolymerComponentColumnMapping,
    },
  });
}

/**
 * Performs dry-run: creating entities from spreadsheet into registry, returning any errors that would
 * result.
 * @param  {String}   registryId             api identifier of the registry that the imported entities will
 *                                           be added to
 * @param  {String[]} linkItemsFolderIds  api identifiers for folders whose files may be linked
 *                                           be added
 * @param  {String}   tagSchemaId            api identifier of the tag schema that the imported entites will
 *                                           be given
 * @param  {String}   projectFolderId        (optional) API ID of project folder to create files in
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                           ColumnType to the id of the corresponding schema field
 * @param {Object}    monomerMapping      mapping of natural base to monomer ID for adding mods to sequences
 */
export function validateSpreadsheetForCreateRegistryEntities(
  spreadsheetParams: SpreadsheetParams,
  registryId: string,
  projectFolderId: string | null,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: string,
  areSequencesCircular: boolean,
  containerSchemaId: Optional<string>,
  {inOrgCollection, inUserCollection, labelingStrategy}: RegistryImportSettings,
  schemaFieldIdsByColumnName: {[key: string]: number},
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE},
  monomerMapping: MonomerMapping | null,
  complexPolymerComponentColumnMapping: {[key: string]: string},
  customSyntaxName?: string
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return net
    .postJSON({
      url: `/1/api/registries/${registryId}/actions/validate-spreadsheet-files`,
      data: {
        ...spreadsheetParams,
        projectFolderId,
        linkItemsFolderIds,
        tagSchemaId,
        areSequencesCircular,
        containerSchemaId,
        inOrgCollection,
        inUserCollection,
        labelingStrategy,
        schemaFieldIdsByColumnName,
        derivedColumnsMapping,
        monomerMapping,
        customSyntaxName,
        complexPolymerComponentColumnMapping,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

/**
 * Performs dry-run: imports mixture ingredients from spreadsheet, returning any errors that would result.
 * @param {String} targetFolderId  api identifier of folder containing mixtures for which to update ingredients
 * @param {String} mixtureSchemaId api identifier of the schema of the mixtures to update
 *
 * @return {jqXHR}                  TODO(BNCH-13522)
 */
export function validateSpreadsheetImportForMixtureIngredients(
  spreadsheetParams: SpreadsheetParams,
  mixturesFolderId: string,
  componentBioentityFolderIds: Array<string>,
  clearIngredientFieldsForEmptyCells: boolean
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return net
    .postJSON({
      url: `/1/api/mixtures:validate-bulk-import-ingredients`,
      data: {
        ...spreadsheetParams,
        mixturesFolderId,
        componentBioentityFolderIds,
        options: {clearIngredientFieldsForEmptyCells},
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export interface ImportMixtureIngredientsFromSpreadsheetResponse {
  folder: FolderSummary;
  mixtureSpecificAttributes: Array<GraphQLTypes['MixtureSpecificAttributes']>;
}

/**
 * Imports mixture ingredients from spreadsheet, returning any errors that would result.
 * @param {String} targetFolderId  api identifier of folder containing mixtures for which to update ingredients
 * @param {String} mixtureSchemaId api identifier of the schema of the mixtures to update
 * @param {Boolean} shouldClearEmptyIngredientCells empty cells will clear the corresponding ingredient data
 *
 * @return {jqXHR}                  TODO(BNCH-13522)
 */
export function importMixtureIngredientsFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  mixturesFolderId: string,
  componentBioentityFolderIds: Array<string>,
  clearIngredientFieldsForEmptyCells: boolean,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<ImportMixtureIngredientsFromSpreadsheetResponse> {
  return net
    .postJSON({
      url: `/1/api/mixtures:bulk-import-ingredients`,
      data: {
        ...spreadsheetParams,
        mixturesFolderId,
        componentBioentityFolderIds,
        options: {clearIngredientFieldsForEmptyCells},
      },
      extraHeaders: dataModsHeader,
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function validateCreateResultsForRun(
  spreadsheetParams: SpreadsheetParams,
  assayRunId: string,
  resultSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number}
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return net
    .postJSON({
      url: `/1/api/assay-runs:validate-create-results-for-run`,
      data: {
        ...spreadsheetParams,
        assayRunId,
        resultSchemaId,
        schemaFieldIdsByColumnName,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function createResultsForRun(
  spreadsheetParams: SpreadsheetParams,
  assayRunId: string,
  resultSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{}> {
  return net
    .postJSON({
      url: `/1/api/assay-runs:create-results-for-run`,
      data: {
        ...spreadsheetParams,
        assayRunId,
        resultSchemaId,
        schemaFieldIdsByColumnName,
      },
      extraHeaders: dataModsHeader,
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function validateUpdatedRowsForDataTableImport(
  spreadsheetParams: SpreadsheetParams,
  tableColumnNamesByColumnName: {[key: string]: string},
  dataTable: unknown
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return net
    .postJSON({
      url: `/1/api/workflows/data-tables:validate-updated-rows-for-import`,
      data: {
        ...spreadsheetParams,
        tableColumnNamesByColumnName,
        dataTable,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export function validateNewRowsForDataTableImport(
  spreadsheetParams: SpreadsheetParams,
  tableColumnNamesByColumnName: {[key: string]: string},
  dataTableConfig: DataTableConfig,
  stageEntryId: string | null
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return net
    .postJSON({
      url: `/1/api/workflows/data-tables:validate-new-rows-for-import`,
      data: {
        ...spreadsheetParams,
        tableColumnNamesByColumnName,
        dataTableConfig,
        stageEntryId, // TODO backend expects stageEntry not stageEntryId
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}
export interface GetUpdatedRowsForDataTableImportResponse {
  importedRows: Array<ImportedRowUpdate>;
}

export function getUpdatedRowsForDataTableImport(
  spreadsheetParams: SpreadsheetParams,
  tableColumnNamesByColumnName: {[key: string]: string},
  dataTable: unknown
): JQueryPromise<GetUpdatedRowsForDataTableImportResponse> {
  const data = {
    ...spreadsheetParams,
    tableColumnNamesByColumnName,
    dataTable,
  };
  return TaskRunner.runTask({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'POST',
    dataType: 'json',
    url: '/1/api/workflows/data-tables:get-updated-rows-for-import',
  });
}

export interface GetNewRowsForDataTableImportResponse {
  importedRows: Array<ImportedDataTableRow>;
}

export function getNewRowsForDataTableImport(
  spreadsheetParams: SpreadsheetParams,
  tableColumnNamesByColumnName: {[key: string]: string},
  dataTableConfig: DataTableConfig,
  stageEntryId: string | null
): JQueryPromise<GetNewRowsForDataTableImportResponse> {
  const data = {
    ...spreadsheetParams,
    tableColumnNamesByColumnName,
    dataTableConfig,
    stageEntryId, // TODO backend wants stageEntry not stageEntryId
  };
  return TaskRunner.runTask({
    contentType: 'application/json',
    data: JSON.stringify(data),
    type: 'POST',
    dataType: 'json',
    url: '/1/api/workflows/data-tables:get-new-rows-for-import',
  });
}

export function validateNewRowsForTransferTableImport(
  spreadsheetParams: SpreadsheetParams,
  schemaFieldIdsByColumnName: {[key: string]: number},
  fileId: string
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: `/1/api/workflows/transfer-tables:validate-new-rows-for-import`,
    data: {
      ...spreadsheetParams,
      schemaFieldIdsByColumnName,
      ...(APIUtil.isStageEntry(fileId) ? {stageEntryId: fileId} : {}),
    },
  });
}

export interface GetNewRowsForTransferTableImportResponse {
  importedRows: Array<ImportedTransferTableRow>;
}

export function getNewRowsForTransferTableImport(
  spreadsheetParams: SpreadsheetParams,
  schemaFieldIdsByColumnName: {[key: string]: number},
  fileId: string
): JQueryPromise<GetNewRowsForTransferTableImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/workflows/transfer-tables:get-new-rows-for-import',
    data: {
      ...spreadsheetParams,
      schemaFieldIdsByColumnName,
      ...(APIUtil.isStageEntry(fileId) ? {stageEntryId: fileId} : {}),
    },
  });
}

export function validateNewRowsForRegistrationTableImport(
  spreadsheetParams: SpreadsheetParams,
  schemaFieldIdsByColumnName: {[key: string]: number}
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: `/1/api/workflows:validate-editable-rows-for-registration-table-import`,
    data: {
      ...spreadsheetParams,
      schemaFieldIdsByColumnName,
    },
  });
}

export interface GetNewRowsForRegistrationTableImportResponse {
  importedRows: Array<ImportedStructuredTableRow>;
}

export function getNewRowsForRegistrationTableImport(
  spreadsheetParams: SpreadsheetParams,
  schemaFieldIdsByColumnName: {[key: string]: number}
): JQueryPromise<GetNewRowsForRegistrationTableImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/workflows:import-editable-rows-for-registration-table',
    data: {
      ...spreadsheetParams,
      schemaFieldIdsByColumnName,
    },
  });
}

export interface GetNewRowsForStructuredTableImportResponse {
  parsedRows: Array<ImportedStructuredTableRow>;
}

export function getNewRowsForStructuredTableImport(
  spreadsheetParams: SpreadsheetParams,
  schemaFieldIdsByColumnName: {[key: string]: number}
): JQueryPromise<GetNewRowsForStructuredTableImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/notebook:parse-rows-for-structured-table-spreadsheet-import',
    data: {
      ...spreadsheetParams,
      schemaFieldIdsByColumnName,
    },
  });
}

export function validateNewRowsForStructuredTableImport(
  spreadsheetParams: SpreadsheetParams,
  schemaFieldIdsByColumnName: {[key: string]: number}
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/notebook:validate-rows-for-structured-table-spreadsheet-import',
    data: {
      ...spreadsheetParams,
      schemaFieldIdsByColumnName,
    },
  });
}

export function validateNewSampleGroups(
  spreadsheetParams: SpreadsheetParams,
  sampleGroupSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sampleTypesByColumnName: {[key: string]: string}
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return net
    .postJSON({
      url: '/1/api/sample-groups:validate-new-sample-groups',
      data: {
        ...spreadsheetParams,
        sampleGroupSchemaId,
        schemaFieldIdsByColumnName,
        sampleTypesByColumnName,
      },
    })
    .then((response) => TaskRunner.resumeTask(response.status_url));
}

export interface GetNewSampleGroupsResponse {
  importedRows: Array<ImportedRow>;
}

export function getNewSampleGroups(
  spreadsheetParams: SpreadsheetParams,
  sampleGroupSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sampleTypesByColumnName: {[key: string]: string}
): JQueryPromise<GetNewSampleGroupsResponse> {
  const data = {
    ...spreadsheetParams,
    sampleGroupSchemaId,
    schemaFieldIdsByColumnName,
    sampleTypesByColumnName,
  };
  return TaskRunner.postJSON({
    url: '/1/api/sample-groups:get-new-sample-groups-for-import',
    data,
  });
}

/**
 * Performs dry-run: updating bioentities from spreadsheet, returning any errors that would result.
 * @param  {String}   tagItemsFolderId    api identifier of folder containing items to be tagged
 * @param  {String[]} linkItemsFolderIds  api identifiers for folders whose files may be linked
 * @param  {String}   tagSchemaId         api identifier of the tag schema that the imported entites will
 *                                        be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {Boolean}  deleteExistingAliases        delete existing aliases
 * @param  {Object}   derivedColumnsMapping mapping of ColumnTransform to COLUMN_TYPE indicating which derived
 *                                        columns to create
 * @param {Object}    monomerMapping      mapping of natural base to monomer ID for adding mods to sequences
 */
export function validateSpreadsheetForUpdateBioentities(
  spreadsheetParams: SpreadsheetParams,
  tagItemsFolderId: string,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  deleteExistingAliases: boolean,
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE},
  monomerMapping: MonomerMapping | null,
  complexPolymerComponentColumnMapping: {[key: string]: string},
  customSyntaxName?: string
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/files:validate-for-bulk-tag',
    data: {
      ...spreadsheetParams,
      tagItemsFolderId,
      linkItemsFolderIds,
      tagSchemaId,
      schemaFieldIdsByColumnName,
      deleteExistingAliases,
      derivedColumnsMapping,
      monomerMapping,
      customSyntaxName,
      complexPolymerComponentColumnMapping,
    },
  });
}

/**
 * Creates inventory files from spreadsheet with the specified tag schema
 * @param  {String}   folderId                 api identifier of the destination folder
 * @param  {String[]} linkItemsFolderIds       api identifiers for folders whose files may be linked
 * @param  {String}   tagSchemaId              api identifier of the tag schema to instantiate
 * @param  {String}   schemalessCreationEntityType               entity type to create, alternate to tagSchemaId
 * @param  {Boolean}  replaceFileLinkWithText  replace file links with text if file not found
 * @param  {Boolean}  areSequencesCircular     create sequences with circular topology
 * @param containerSchemaId
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                             ColumnType to the id of the corresponding schema field
 * @param  {Object}   derivedColumnsMapping mapping of ColumnTransform to COLUMN_TYPE indicating which derived
 *                                        columns to create
 * @param {Object}    monomerMapping      mapping of natural base to monomer ID for adding mods to sequences
 * @param {Object}    dataModsHeader      header with data modifications information to be sent from the Data Mods modal
 */
export function createInventoryFilesFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  folderId: string,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: Optional<string>,
  schemalessCreationEntityType: SchemalessBulkImportSupportedOligoAwareTypes | null,
  replaceFileLinkWithText: boolean,
  areSequencesCircular: boolean,
  containerSchemaId: Optional<string>,
  schemaFieldIdsByColumnName: {[key: string]: number},
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE},
  monomerMapping: MonomerMapping | null,
  complexPolymerComponentColumnMapping: {[key: string]: string},
  customSyntaxName?: string,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{folder: FolderSummary}> {
  const data = {
    ...spreadsheetParams,
    folderId,
    linkItemsFolderIds,
    tagSchemaId,
    schemalessCreationEntityType,
    replaceFileLinkWithText,
    areSequencesCircular,
    containerSchemaId,
    schemaFieldIdsByColumnName,
    derivedColumnsMapping,
    monomerMapping,
    customSyntaxName,
    complexPolymerComponentColumnMapping,
  };
  return TaskRunner.postJSON({
    url: '/1/api/inventory-files:bulk-create',
    data,
    extraHeaders: dataModsHeader,
  });
}

/**
 * Creates entities from spreadsheet with the specified tag schema into a registry
 * @param  {Object}   registry            registry that the objects will be added to
 * @param  {String[]} linkItemsFolderIds  api identifiers for folders whose files may be linked
 * @param  {String}   tagSchemaId         api identifier of the tag schema that the imported entites will
 *                                        be given
 * @param  {String}   projectFolderId     (optional) API ID of project folder to create files in
 * @param  {Boolean}  inOrgCollection     whether or not the imported entity should be added to the
 *                                        registry's organization collection
 * @param  {Boolean}  inUserCollection    whether or not the imported entity should be added to the
 *                                        registry's collection for the current user
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {Boolean}  replaceFileLinkWithText use text value for any linked items options that cannot be
 *                                        matched
 * @param  {Object}   derivedColumnsMapping mapping of ColumnTransform to COLUMN_TYPE indicating which derived
 *                                        columns to create
 * @param {Object}    monomerMapping      mapping of natural base to monomer ID for adding mods to sequences
 * @param  {Object}   dataModsHeader      header with data modifications information to be sent from the Data Mods modal
 */
export function createRegistryEntitiesFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  registry: Registry,
  projectFolderId: string | null,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: string,
  areSequencesCircular: boolean,
  containerSchemaId: Optional<string>,
  replaceFileLinkWithText: boolean,
  {inOrgCollection, inUserCollection, labelingStrategy}: RegistryImportSettings,
  schemaFieldIdsByColumnName: {[key: string]: number},
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE},
  monomerMapping: MonomerMapping | null,
  complexPolymerComponentColumnMapping: {[key: string]: string},
  customSyntaxName?: string,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<{folder: FolderSummary}> {
  const data = {
    ...spreadsheetParams,
    registry,
    projectFolderId,
    linkItemsFolderIds,
    tagSchemaId,
    areSequencesCircular,
    containerSchemaId,
    inOrgCollection,
    inUserCollection,
    labelingStrategy,
    schemaFieldIdsByColumnName,
    replaceFileLinkWithText,
    derivedColumnsMapping,
    monomerMapping,
    customSyntaxName,
    complexPolymerComponentColumnMapping,
  };
  return TaskRunner.postJSON({
    data,
    url: `/1/api/registries/${registry.id}/actions/import-spreadsheet-files`,
    extraHeaders: dataModsHeader,
  });
}

interface TrialSamplesBulkImportArgs {
  spreadsheetParams: SpreadsheetParams;
  registry: Registry;
  projectFolderId: string | null;
  linkItemsFolderIds: Array<string>;
  tagSchemaId: string;
  trialSubjectSchemaId: string;
  areSequencesCircular: boolean;
  containerSchemaId: string;
  replaceFileLinkWithText: boolean;
  registryImportSettings: RegistryImportSettings;
  schemaFieldIdsByColumnName: {[key: string]: number};
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE};
  monomerMapping: MonomerMapping | null;
  complexPolymerComponentColumnMapping: {[key: string]: string};
  customSyntaxName?: string;
}

export interface CreateTrialSamplesFromSpreadsheetSuccessResponse {
  start: string;
  finish: string;
  creator: string;
  containers: Array<{id: string; barcode: string; name: string}>;
  newSamples: Array<{id: string; name: string}>;
  newSubjects: Array<{id: string; name: string}>;
  discrepancies: Array<{id: string; name: string}>;
  testOrders: Array<{id: string; name: string}>;
  studies: Array<{id: string}>;
}

export function createTrialSamplesFromSpreadsheet({
  spreadsheetParams,
  registry,
  projectFolderId,
  linkItemsFolderIds,
  tagSchemaId,
  trialSubjectSchemaId,
  areSequencesCircular,
  containerSchemaId,
  replaceFileLinkWithText,
  registryImportSettings,
  schemaFieldIdsByColumnName,
  derivedColumnsMapping,
  monomerMapping,
  complexPolymerComponentColumnMapping,
  customSyntaxName,
  dataModsHeader,
}: TrialSamplesBulkImportArgs & {
  dataModsHeader?: DataModificationsHeader;
}): JQueryPromise<CreateTrialSamplesFromSpreadsheetSuccessResponse> {
  const {inOrgCollection, inUserCollection, labelingStrategy, trialSubjectLabelingStrategy} =
    registryImportSettings;
  const data = {
    ...spreadsheetParams,
    registry,
    projectFolderId,
    linkItemsFolderIds,
    tagSchemaId,
    trialSubjectSchemaId,
    areSequencesCircular,
    containerSchemaId,
    inOrgCollection,
    inUserCollection,
    labelingStrategy,
    trialSubjectLabelingStrategy,
    schemaFieldIdsByColumnName,
    replaceFileLinkWithText,
    derivedColumnsMapping,
    monomerMapping,
    customSyntaxName,
    complexPolymerComponentColumnMapping,
  };
  return TaskRunner.postJSON(
    {
      data,
      url: `/1/api/registries/${registry.id}/actions/import-trial-samples-spreadsheet`,
      extraHeaders: dataModsHeader,
    },
    CREATE_TRIAL_SAMPLE_MAX_TOTAL_WAIT_TIME_MS
  );
}

export function validateSpreadsheetForTrialSamples({
  spreadsheetParams,
  registry,
  projectFolderId,
  linkItemsFolderIds,
  tagSchemaId,
  trialSubjectSchemaId,
  areSequencesCircular,
  containerSchemaId,
  replaceFileLinkWithText,
  registryImportSettings,
  schemaFieldIdsByColumnName,
  derivedColumnsMapping,
  monomerMapping,
  complexPolymerComponentColumnMapping,
  customSyntaxName,
}: TrialSamplesBulkImportArgs): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  const {inOrgCollection, inUserCollection, labelingStrategy, trialSubjectLabelingStrategy} =
    registryImportSettings;
  const data = {
    ...spreadsheetParams,
    registry,
    projectFolderId,
    linkItemsFolderIds,
    tagSchemaId,
    trialSubjectSchemaId,
    areSequencesCircular,
    containerSchemaId,
    inOrgCollection,
    inUserCollection,
    labelingStrategy,
    trialSubjectLabelingStrategy,
    schemaFieldIdsByColumnName,
    replaceFileLinkWithText,
    derivedColumnsMapping,
    monomerMapping,
    customSyntaxName,
    complexPolymerComponentColumnMapping,
  };
  return TaskRunner.postJSON(
    {
      data,
      url: `/1/api/registries/${registry.id}/actions/validate-trial-samples-spreadsheet`,
    },
    CREATE_TRIAL_SAMPLE_MAX_TOTAL_WAIT_TIME_MS
  );
}

export interface UpdateBioentitiesFromSpreadsheetResponse {
  folder: FolderSummary;
  data: Array<{
    bioentityAliasInfos: Array<BioentityAliasInfo>;
    id: string;
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tags: Array<any>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    note?: any;
  }>;
}

/**
 * Updates bioentities from spreadsheet
 * @param  {String}   tagItemsFolderId         api identifier of folder containing items to be tagged
 * @param  {String[]} linkItemsFolderIds       api identifiers for folders whose files may be linked
 * @param  {String}   tagSchemaId              api identifier of the tag schema that the set on the
 *                                             tagged items
 * @param  {Boolean}  replaceFileLinkWithText  replace file links with text if file not found
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                             ColumnType to the id of the corresponding schema field
 * @param  {Boolean}  deleteEmptyFields        delete field values with empty cells
 * @param  {Object}   derivedColumnsMapping mapping of ColumnTransform to COLUMN_TYPE indicating which derived
 *                                        columns to create
 * @param  {Object}   dataModsHeader      header with data modifications information to be sent from the Data Mods modal
 */
export function updateBioentitiesFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  tagItemsFolderId: string,
  linkItemsFolderIds: Array<string>,
  tagSchemaId: Optional<string>,
  replaceFileLinkWithText: boolean,
  schemaFieldIdsByColumnName: {[key: string]: number},
  deleteEmptyFields: boolean,
  deleteExistingAliases: boolean,
  derivedColumnsMapping: {[key in ColumnTransform]?: COLUMN_TYPE},
  monomerMapping: MonomerMapping | null,
  complexPolymerComponentColumnMapping: {[key: string]: string},
  customSyntaxName?: string,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<UpdateBioentitiesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    data: {
      ...spreadsheetParams,
      tagItemsFolderId,
      linkItemsFolderIds,
      tagSchemaId,
      replaceFileLinkWithText,
      schemaFieldIdsByColumnName,
      deleteEmptyFields,
      deleteExistingAliases,
      derivedColumnsMapping,
      monomerMapping,
      customSyntaxName,
      complexPolymerComponentColumnMapping,
    },
    url: '/1/api/files/bulk-tag',
    extraHeaders: dataModsHeader,
  });
}

/**
 * Performs dry-run: transfer storables from spreadsheet, returning any errors that would result.
 */
export function validateSpreadsheetForTransferStorables(
  spreadsheetParams: SpreadsheetParams,
  clearSampleRolesForEmptyCells: boolean
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/storables:validate-for-transfer-from-spreadsheet',
    data: {...spreadsheetParams, clearSampleRolesForEmptyCells},
  });
}

export interface TransferStorablesFromSpreadsheetResponse {
  folder: FolderSummary;
  invalidatableStorableIds: Array<string>;
}

/**
 * Transfer storables from spreadsheet
 */
export function transferStorablesFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  clearSampleRolesForEmptyCells: boolean,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<TransferStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    data: {...spreadsheetParams, clearSampleRolesForEmptyCells},
    url: '/1/api/storables:bulk-transfer',
    extraHeaders: dataModsHeader,
  });
}

export interface CreateOrUpdateStorablesFromSpreadsheetResponse {
  folder: FolderSummary;
}

/**
 * Performs dry-run: creating containers from spreadsheet into registry, returning any errors that would
 * result.
 * @param  {String}   containerSchemaId   api identifier of the container schema that the imported containers
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported containers will be
 *                                        associated with for permissions
 */
export function validateSpreadsheetForCreateContainers(
  spreadsheetParams: SpreadsheetParams,
  containerSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/containers:validate-spreadsheet-for-bulk-create',
    data: {
      ...spreadsheetParams,
      containerSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
    },
  });
}

/**
 * Creates containers from spreadsheet with the specified container schema into a registry
 * @param  {String}   containerSchemaId   api identifier of the container schema that the imported containers
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported containers will be
 *                                        associated with for permissions
 */
export function createContainersFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  containerSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>
): JQueryPromise<CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/containers:bulk-create',
    data: {
      ...spreadsheetParams,
      containerSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
    },
  });
}

/**
 * Performs dry-run: updating containers from spreadsheet, returning any errors that would
 * result.
 * @param  {String}   containerSchemaId   api identifier of the updated containers' container schema
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {Boolean}  deleteEmptyFields   delete field values with empty cells
 * @param  {Boolean}  clearSampleRolesForEmptyCells  clear existing sample roles for empty cells
 */
export function validateSpreadsheetForUpdateContainers(
  spreadsheetParams: SpreadsheetParams,
  containerSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  linkItemsFolderIds: Array<string>,
  deleteEmptyFields: boolean,
  clearSampleRolesForEmptyCells: boolean
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/containers:validate-spreadsheet-for-bulk-update',
    data: {
      ...spreadsheetParams,
      containerSchemaId,
      schemaFieldIdsByColumnName,
      linkItemsFolderIds,
      deleteEmptyFields,
      clearSampleRolesForEmptyCells,
    },
  });
}

/**
 * Updates containers from spreadsheet
 * @param  {String}   containerSchemaId   api identifier of the updated containers' container schema
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {Boolean}  deleteEmptyFields   delete field values with empty cells
 * @param  {Boolean}  clearSampleRolesForEmptyCells  clear existing sample roles for empty cells
 * @param  {Object}   dataModsHeader      header with data modifications information to be sent from the Data Mods modal
 */
export function updateContainersFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  containerSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  linkItemsFolderIds: Array<string>,
  deleteEmptyFields: boolean,
  clearSampleRolesForEmptyCells: boolean,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/containers:bulk-update',
    data: {
      ...spreadsheetParams,
      containerSchemaId,
      schemaFieldIdsByColumnName,
      linkItemsFolderIds,
      deleteEmptyFields,
      clearSampleRolesForEmptyCells,
    },
    extraHeaders: dataModsHeader,
  });
}

/**
 * Performs dry-run: creating grids from spreadsheet into registry, returning any errors that would
 * result.
 * @param  {String}   gridSchemaId        api identifier of the grid schema that the imported grids
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported grids will be
 *                                        associated with for permissions
 */
export function validateSpreadsheetForCreateGrids(
  spreadsheetParams: SpreadsheetParams,
  gridSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/grids:validate-spreadsheet-for-bulk-create',
    data: {
      ...spreadsheetParams,
      gridSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
    },
  });
}

/**
 * Performs dry-run: creating locations from spreadsheet into registry, returning any errors that would
 * result.
 * @param  {String}   locationSchemaId    api identifier of the location schema that the imported locations
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported locations will be
 *                                        associated with for permissions
 */
export function validateSpreadsheetForCreateLocations(
  spreadsheetParams: SpreadsheetParams,
  locationSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/locations:validate-spreadsheet-for-bulk-create',
    data: {
      ...spreadsheetParams,
      locationSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
    },
  });
}

/**
 * Creates grids from spreadsheet with the specified grid schema into a registry
 * @param  {String}   gridSchemaId        api identifier of the grid schema that the imported grids
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported grids will be
 *                                        associated with for permissions
 */
export function createGridsFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  gridSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>
): JQueryPromise<CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/grids:bulk-create',
    data: {
      ...spreadsheetParams,
      gridSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
    },
  });
}

/**
 * Creates locations from spreadsheet with the specified location schema into a registry
 * @param  {String}   locationSchemaId        api identifier of the location schema that the imported locations
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported locations will be
 *                                        associated with for permissions
 */
export function createLocationsFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  locationSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>
): JQueryPromise<CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/locations:bulk-create',
    data: {
      ...spreadsheetParams,
      locationSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
    },
  });
}

/**
 * Performs dry-run: updating grids from spreadsheet, returning any errors that would result.
 *
 * @param  {Boolean}  deleteEmptyFields   delete field values with empty cells
 */
export function validateSpreadsheetForUpdateGrids(
  spreadsheetParams: SpreadsheetParams,
  gridSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>,
  deleteEmptyFields: boolean
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/grids:validate-spreadsheet-for-bulk-update',
    data: {
      ...spreadsheetParams,
      gridSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
      deleteEmptyFields,
    },
  });
}

/**
 * Updates grids from spreadsheet.
 *
 * @param  {Boolean}  deleteEmptyFields   delete field values with empty cells
 * @param  {Object}   dataModsHeader      header with data modifications information to be sent from the Data Mods modal
 */
export function updateGridsFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  gridSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>,
  deleteEmptyFields: boolean,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/grids:bulk-update',
    data: {
      ...spreadsheetParams,
      gridSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
      deleteEmptyFields,
    },
    extraHeaders: dataModsHeader,
  });
}

/**
 * Performs dry-run: updating locations from spreadsheet, returning any errors that would result.
 *
 * @param  {Object}   spreadsheetParams   dict of information about spreadsheet itself (location, columns,
 *                                        filename, rows to skip, time limit)
 * @param  {String}   locationSchemaId    api identifier of the location schema that the imported locations
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported locations will be
 *                                        associated with for permissions
 * @param  {String[]} linkItemsFolderIds  api identifiers for folders whose files may be linked
 * @param  {Boolean}  deleteEmptyFields   delete field values with empty cells
 */
export function validateSpreadsheetForUpdateLocations(
  spreadsheetParams: SpreadsheetParams,
  locationSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>,
  deleteEmptyFields: boolean
): JQueryPromise<ValidateSpreadsheetForBulkImportResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/locations:validate-spreadsheet-for-bulk-update',
    data: {
      ...spreadsheetParams,
      locationSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
      deleteEmptyFields,
    },
  });
}

/**
 * Updates locations from spreadsheet.
 *
 * @param  {Object}   spreadsheetParams   dict of information about spreadsheet itself (location, columns,
 *                                        filename, rows to skip, time limit)
 * @param  {String}   locationSchemaId    api identifier of the location schema that the imported locations
 *                                        will be given
 * @param  {Object}   schemaFieldIdsByColumnName a map that maps column names that have SCHEMA_FIELD
 *                                        ColumnType to the id of the corresponding schema field
 * @param  {String}   sourceId            api identifier of the source that the imported locations will be
 *                                        associated with for permissions
 * @param  {String[]} linkItemsFolderIds  api identifiers for folders whose files may be linked
 * @param  {Boolean}  deleteEmptyFields   delete field values with empty cells
 * @param {Object}    dataModsHeader      header with data modifications information to be sent from the Data Mods modal
 */
export function updateLocationsFromSpreadsheet(
  spreadsheetParams: SpreadsheetParams,
  locationSchemaId: string,
  schemaFieldIdsByColumnName: {[key: string]: number},
  sourceId: Optional<string>,
  linkItemsFolderIds: Array<string>,
  deleteEmptyFields: boolean,
  dataModsHeader?: DataModificationsHeader
): JQueryPromise<CreateOrUpdateStorablesFromSpreadsheetResponse> {
  return TaskRunner.postJSON({
    url: '/1/api/locations:bulk-update',
    data: {
      ...spreadsheetParams,
      locationSchemaId,
      schemaFieldIdsByColumnName,
      sourceId,
      linkItemsFolderIds,
      deleteEmptyFields,
    },
    extraHeaders: dataModsHeader,
  });
}

/**
 * Downloads invalid molecules from SDF import.
 *
 * @param {string} url S3 location of parsed SD file as CSV
 * @param {Array<integer>} rowsToDownload indices of failing rows
 */
export function downloadInvalidMolecules(
  url: string,
  rowsToDownload: Array<{rowIndex: number; errorMessage: string}>
): Promise<{s3Location: string}> {
  return net.postJSONPromise({
    url: '/1/api/molecules/download-invalid-molecules',
    data: {
      url,
      rowsToDownload,
    },
  });
}

/**
 * Saves column mappings in a spreadsheet import flow.
 *
 * @param {SpreadsheetColumnsMapping} spreadsheetColumnsMapping spreadsheet column mapping to save
 */
export function saveSpreadsheetColumnsMapping(
  spreadsheetColumnsMapping: SpreadsheetColumnsMapping
): Promise<{spreadsheetMappingId: string}> {
  return net.postJSONPromise({
    url: '/1/api/files/create-spreadsheet-mappings',
    data: {
      spreadsheetColumnsMapping,
    },
  });
}
